import { DEFAULT_APPEARANCE } from '@/theme/engine.mjs';
import { printerMode } from "@/lib/printer-mode";
import { variantSummary } from "@/lib/variant-quantities.mjs";
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { rupiah } from "@/lib/format";
import { api, apiError } from "@/lib/pos-api";
import { printViaBluetooth } from "@/lib/btprint";
import { printReceipt } from "@/lib/printReceipt";
import { cacheSettings, readSettings } from "@/lib/offline";
import { toast } from "sonner";
import { Printer, Check, X, Bluetooth, Wifi, Loader2 } from "lucide-react";

const methodLabel = { tunai: "Tunai", transfer: "Transfer", qris: "QRIS", kartu_debit: "Kartu Debit", kartu_kredit: "Kartu Kredit" };
const mLabel = (m) => methodLabel[m] || (m ? m.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : m);

export default function ReceiptModal({ tx, cashier, onClose }) {
  const [settings, setSettings] = useState(null);
  const [settingsFor, setSettingsFor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [printedFor, setPrintedFor] = useState(null);

  useEffect(() => {
    if (!tx) return;
    let current = true;
    api.get("/settings").then((r) => {
      if (!current) return;
      setSettings(r.data); setSettingsFor(tx); cacheSettings(r.data);
    }).catch(() => {
      if (!current) return;
      setSettings(readSettings()); setSettingsFor(tx);
    });
    return () => { current = false; };
  }, [tx]);

  // Auto-cetak SENYAP ke printer Ethernet saat transaksi berhasil (tanpa jendela/konfirmasi browser).
  useEffect(() => {
    if (!tx || settings === null || settingsFor !== tx) return;
    if (settings.auto_print === false) return;
    if (printedFor === tx.id) return;
    setPrintedFor(tx.id);
    const s2 = settings || {};
    const mode = printerMode(s2);
    if (mode === "ethernet" && s2.eth_ip) {
      api.post("/print/ethernet", { transaction_id: tx.id })
        .then(() => toast.success("Struk tercetak"))
        .catch((e) => toast.error("Auto-cetak gagal: " + apiError(e)));
    } else if (mode === "bluetooth") {
      printViaBluetooth(tx, s2, cashier).then(() => toast.success("Struk tercetak")).catch(() => {});
    } else if (mode === "browser") {
      printReceipt(tx, s2, cashier);
    }
    // eslint-disable-next-line
  }, [tx, settings, settingsFor]);

  if (!tx) return null;
  const s = settings || {};
  const date = new Date(tx.created_at);
  const dateStr = date.toLocaleString("id-ID", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });

  const printBt = async () => {
    setBusy(true);
    try {
      await printViaBluetooth(tx, s, cashier);
      toast.success("Dikirim ke printer Bluetooth");
    } catch (e) {
      toast.error(e.message || "Gagal cetak Bluetooth");
    } finally {
      setBusy(false);
    }
  };

  const printEth = async () => {
    setBusy(true);
    try {
      if (tx.invoice_no?.includes("(REVISI)") || tx.offline) await api.post("/print/draft", tx);
      else await api.post("/print/ethernet", { transaction_id: tx.id });
      toast.success("Dikirim ke printer Ethernet");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const printConfigured = () => {
    if (busy || settingsFor !== tx || settings === null) return;
    const mode = printerMode(s);
    if (mode === "ethernet") {
      if (!s.eth_ip) { toast.error("Isi alamat IP printer di Pengaturan Printer terlebih dahulu"); return; }
      return printEth();
    }
    if (mode === "bluetooth") return printBt();
    if (!printReceipt(tx, s, cashier)) toast.error("Izinkan popup browser untuk mencetak struk");
  };

  return (
    <Dialog open={!!tx} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="thermal-receipt-modal"
        className="bg-ui-card border-ui-line text-ui-text max-w-sm p-0 overflow-hidden"
      >
        <DialogTitle className="sr-only">Struk Pembayaran {tx.invoice_no}</DialogTitle>
        <DialogDescription className="sr-only">Detail struk transaksi dan tombol cetak</DialogDescription>
        <div className="p-4 flex items-center gap-2 bg-brand-soft border-b border-brand/20">
          <div className="h-9 w-9 rounded-full bg-brand-button flex items-center justify-center">
            <Check className="h-5 w-5 text-ui-text" />
          </div>
          <div>
            <div className="font-heading font-bold">Pembayaran Berhasil</div>
            <div className="text-xs text-ui-muted">Kembalian {rupiah(tx.change)}</div>
          </div>
        </div>

        {/* Printable thermal area */}
        <div className="p-4 max-h-[55vh] overflow-y-auto">
          <div id="thermal-print" className="bg-ui-card text-black rounded-lg p-4 font-mono text-[12px] leading-tight">
            <div className="text-center">
              <div className="font-bold text-base">{s.store_name || DEFAULT_APPEARANCE.branding.name}</div>
              {s.address ? <div className="text-[10px]">{s.address}</div> : null}
              {s.phone ? <div className="text-[10px]">Telp {s.phone}</div> : null}
            </div>
            <div className="border-t border-dashed border-black my-2" />
            <div className="flex justify-between text-[11px]">
              <span>No: {tx.invoice_no}</span>
            </div>
            <div className="flex justify-between text-[11px]">
              <span>{dateStr}</span>
            </div>
            <div className="flex justify-between text-[11px]">
              <span>Kasir: {cashier}</span>
            </div>
            <div className="border-t border-dashed border-black my-2" />
            {tx.items.map((it, i) => (
              <div key={i} className="mb-1">
                <div>{it.name}</div>
                {variantSummary(it) && <div data-testid={`receipt-variants-${i}`}>{variantSummary(it)}</div>}
                <div className="flex justify-between">
                  <span>{it.qty} x {rupiah(it.price)} <span className="uppercase">({it.price_level.slice(0,3)})</span></span>
                  <span>{rupiah(it.qty * it.price)}</span>
                </div>
                {it.discount > 0 && (
                  <div className="flex justify-between"><span>&nbsp;&nbsp;Diskon item</span><span>-{rupiah(it.discount)}</span></div>
                )}
                {it.note ? <div className="italic">&nbsp;&nbsp;* {it.note}</div> : null}
              </div>
            ))}
            <div className="border-t border-dashed border-black my-2" />
            <Row label="Jumlah Produk" value={tx.items.length} />
            <Row label="Subtotal" value={rupiah(tx.subtotal)} />
            {tx.discount > 0 && <Row label="Diskon" value={"-" + rupiah(tx.discount)} />}
            {(tx.previous_debts || []).map(debt => <Row key={debt.invoice_id} label={`Piutang sebelumnya (${debt.invoice_no.slice(-4)})`} value={rupiah(debt.amount)} />)}
            <div className="flex justify-between font-bold text-sm mt-1">
              <span>TOTAL</span>
              <span>{rupiah(tx.total + (tx.settlement_total || 0))}</span>
            </div>
            {tx.fee > 0 && (
              <>
                <Row label="Biaya" value={"+" + rupiah(tx.fee)} />
                <div className="flex justify-between font-bold text-sm">
                  <span>TOTAL BAYAR</span>
                  <span>{rupiah(tx.grand_total || (tx.total + tx.fee))}</span>
                </div>
              </>
            )}
            {tx.return_credit_applied > 0 && <Row label="Saldo Retur" value={"-" + rupiah(tx.return_credit_applied)} />}
            <div className="border-t border-dashed border-black my-2" />
            {(tx.checkout_payments || tx.payments).map((p, i) => (
              <Row key={i} label={mLabel(p.method)} value={rupiah(p.amount)} />
            ))}
            <Row label="Bayar" value={rupiah(tx.paid)} />
            <Row label="Kembali" value={rupiah(tx.change)} />
            {tx.customer_name ? <Row label="Pelanggan" value={tx.customer_name} /> : null}
            {tx.credit > 0 ? (
              <div className="flex justify-between font-bold text-[12px] mt-1">
                <span>SISA PIUTANG</span>
                <span>{rupiah(tx.credit)}</span>
              </div>
            ) : null}
            <div className="border-t border-dashed border-black my-2" />
            {tx.note && <div data-testid="receipt-note" className="mb-2 whitespace-pre-wrap break-words">Catatan: {tx.note}</div>}
            <div className="text-center text-[11px] whitespace-pre-line">
              {s.footer || "*** TERIMA KASIH ***"}
            </div>
          </div>
        </div>

        <div className="p-4 space-y-2 border-t border-ui-line">
          <div className="grid grid-cols-2 gap-2">
            <Button
              data-testid="print-receipt-btn"
              onClick={printConfigured}
              disabled={busy || settingsFor !== tx || settings === null}
              className="h-12 bg-brand-button hover:bg-brand-button text-ui-text font-bold gap-2"
            >
              <Printer className="h-4 w-4" /> Cetak
            </Button>
            <Button
              data-testid="print-bt-btn"
              onClick={printBt}
              disabled={busy}
              variant="outline"
              className="h-12 border-ui-line bg-transparent hover:bg-ui-card gap-2"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bluetooth className="h-4 w-4 text-accent-readable" />} Bluetooth
            </Button>
          </div>
          {s.eth_ip ? (
            <Button
              data-testid="print-eth-btn"
              onClick={printEth}
              disabled={busy}
              variant="outline"
              className="w-full h-11 border-ui-line bg-transparent hover:bg-ui-card gap-2"
            >
              <Wifi className="h-4 w-4 text-brand-readable" /> Cetak Ethernet ({s.eth_ip})
            </Button>
          ) : null}
          <Button
            data-testid="close-receipt-btn"
            variant="ghost"
            onClick={onClose}
            className="w-full h-10 text-ui-muted hover:text-ui-text gap-2"
          >
            <X className="h-4 w-4" /> Tutup
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-[11px]">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
